if (!customElements.get('product-offer-countdown')) {
  customElements.define('product-offer-countdown', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.timer = this.querySelector('[role="timer"]');
      this.offer = this.querySelector('[data-offer-content]');
      this.timerRow = this.querySelector('[data-offer-timer-row]');
      this.timerLabel = this.querySelector('[data-offer-timer-label]');
      this.weekly = this.dataset.countdownMode === 'weekly';
      if (this.weekly) {
        this.deadline = this.readWeeklySchedule() ? this.nextReset(Date.now()) : NaN;
      } else {
        // Require an explicit timezone so every visitor sees the same deadline.
        const value = (this.dataset.endAt || '').trim().replace(/\s*T\s*/, 'T');
        this.deadline = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})$/.test(value)
          ? Date.parse(value) : NaN;
      }
      if (!this.timer || !Number.isFinite(this.deadline)) return;
      this.timer.hidden = false;
      if (this.timerRow) this.timerRow.hidden = false;
      if (this.update()) this.interval = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      this.interval = undefined;
    }

    readWeeklySchedule() {
      const days = (this.dataset.resetDays || '').trim();
      const time = (this.dataset.resetTime || '').trim();
      if (!/^[0-6](?:\s*,\s*[0-6])*$/.test(days) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return false;
      this.resetDays = new Set(days.split(',').map(Number));
      [this.resetHour, this.resetMinute] = time.split(':').map(Number);
      try {
        this.dateFormatter = new Intl.DateTimeFormat('en-GB', {
          timeZone: (this.dataset.resetTimezone || '').trim(),
          calendar: 'gregory',
          numberingSystem: 'latn',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
        });
      } catch {
        return false;
      }
      return true;
    }

    dateParts(timestamp) {
      const parts = {};
      this.dateFormatter.formatToParts(timestamp).forEach(({ type, value }) => {
        if (type !== 'literal') parts[type] = Number(value);
      });
      return parts;
    }

    wallTimestamp(parts) {
      return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second || 0);
    }

    resetTimestamp(parts) {
      const target = this.wallTimestamp(parts);
      // Check offsets on both sides of a possible daylight-saving transition.
      const offsets = new Set([-86400000, 0, 86400000].map((difference) => {
        const probe = target + difference;
        return this.wallTimestamp(this.dateParts(probe)) - probe;
      }));
      const candidates = Array.from(offsets, (offset) => target - offset)
        .map((timestamp) => ({ timestamp, wall: this.wallTimestamp(this.dateParts(timestamp)) }));
      const matches = candidates.filter(({ wall }) => wall === target);
      // A repeated local time resets once, at its first occurrence.
      if (matches.length) return Math.min(...matches.map(({ timestamp }) => timestamp));
      // If the clocks skip this local time, move it forward by the DST gap.
      const afterGap = candidates.filter(({ wall }) => wall > target)
        .sort((a, b) => a.wall - b.wall);
      return afterGap.length ? afterGap[0].timestamp : NaN;
    }

    nextReset(now) {
      const today = this.dateParts(now);
      const date = new Date(Date.UTC(today.year, today.month - 1, today.day));
      for (let day = 0; day <= 7; day += 1) {
        if (this.resetDays.has(date.getUTCDay())) {
          const timestamp = this.resetTimestamp({
            year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate(),
            hour: this.resetHour, minute: this.resetMinute,
          });
          if (timestamp > now) return timestamp;
        }
        date.setUTCDate(date.getUTCDate() + 1);
      }
      return NaN;
    }

    update() {
      const now = Date.now();
      if (this.weekly && this.deadline <= now) this.deadline = this.nextReset(now);
      const remaining = this.deadline - now;
      if (this.offer) this.offer.hidden = remaining <= 0;
      if (this.timerLabel) this.timerLabel.hidden = remaining <= 0;
      if (remaining <= 0) {
        this.timer.textContent = this.dataset.expiredMessage;
        this.disconnectedCallback();
        return false;
      }
      const values = {
        days: Math.floor(remaining / 86400000),
        hours: Math.floor(remaining % 86400000 / 3600000),
        minutes: Math.floor(remaining % 3600000 / 60000),
        seconds: Math.floor(remaining % 60000 / 1000),
      };
      this.timer.textContent = this.dataset.timerFormat.replace(/\[(days|hours|minutes|seconds)\]/g, (_, unit) => values[unit]);
      return true;
    }
  });
}
